import {
  EDocMetadata as BaseEDocMetadata,
  EDocMetadataProps as BaseProps,
} from '@/components/EDocMetadata';
import { FunctionHeader } from '@/components/FunctionHeader';
import { useGetEDocPlan } from '@/context/services/e_doc_plan/EDocPlan.service';
import type { ReceivedInvoiceReceiptType } from '@/context/services/invoice/type';
import { useGetScanDecision } from '@/features/invoices/components/Details/Components/hooks/useScanDecision';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useTranslation } from '@/i18n';
import type { EDocMetaDatum } from 'ap-openapi/openapi/model/eDocMetaDatum';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import styles from './EDocMetadata.module.scss';
type Props = {
  eDocMetaDatum?: EDocMetaDatum;
  receiptType?: ReceivedInvoiceReceiptType;
};

export const EDocMetadata: FC<Props> = memo(
  ({ eDocMetaDatum, receiptType }) => {
    const { t } = useTranslation();
    const cx = classnames.bind(styles);
    const flagEdocMetadata = useGetFlags('E_DOC_METADATA');
    const { apReceiptTypeAlertType } = useGetEDocPlan();
    const scanDecision = useGetScanDecision(
      eDocMetaDatum,
      apReceiptTypeAlertType,
      true
    );

    const metadata = useMemo(() => {
      if (!flagEdocMetadata || !eDocMetaDatum) return null;

      return {
        dpi: eDocMetaDatum.dpi_text ?? '',
        pixelNumber: eDocMetaDatum.pixel_number_text ?? '',
        imageDepth: eDocMetaDatum.rgb_depth_text ?? '',
        type:
          scanDecision === 'warning' || scanDecision === 'error'
            ? scanDecision
            : undefined,
        isScan: eDocMetaDatum.is_scan ?? false,
        isSatisfyResolutionRequirement:
          eDocMetaDatum.is_satisfy_resolution_requirement ?? false,
        isSatisfyDepthRequirements:
          eDocMetaDatum.is_satisfy_depth_requirements ?? false,
        receiptType: receiptType ?? undefined,
      } satisfies BaseProps;
    }, [eDocMetaDatum, flagEdocMetadata, scanDecision, receiptType]);

    if (!flagEdocMetadata || !eDocMetaDatum || !metadata) return null;

    return (
      <FunctionHeader
        functionName={t('e_doc_information')}
        testId='e_doc-metadata-header'
      >
        <div className={cx(styles['contentContainer'])}>
          <BaseEDocMetadata
            dpi={metadata.dpi}
            pixelNumber={metadata.pixelNumber}
            imageDepth={metadata.imageDepth}
            type={metadata.type}
            isScan={metadata.isScan}
            isSatisfyResolutionRequirement={
              metadata.isSatisfyResolutionRequirement
            }
            isSatisfyDepthRequirements={metadata.isSatisfyDepthRequirements}
            receiptType={receiptType}
          />
        </div>
      </FunctionHeader>
    );
  }
);

EDocMetadata.displayName = 'EDocMetadata';
